import { useEffect, useMemo } from "react";
import type { FocusEventHandler, PointerEventHandler } from "react";
import { create } from "zustand";
import { usePresentationStore } from "@/stores/presentation-store";

const FOCUS_CHROME_HIDE_DELAY_MS = 260;
const FOCUS_CHROME_REGION_SELECTOR = "[data-focus-chrome-region='true']";
const FOCUS_CHROME_FLOATING_SELECTOR = [
  "[data-testid='context-menu-content'][data-state='open']",
  "[data-testid='input-bar-conversation-context'][data-state='open']",
  "[data-radix-popper-content-wrapper]",
  "[data-interactive='true'][role='menu'][data-state='open']",
].join(",");
let hideTimer: ReturnType<typeof setTimeout> | null = null;

interface FocusChromeAutoHideState {
  revealed: boolean;
  reveal: () => void;
  scheduleHide: () => void;
  reset: () => void;
}

function clearHideTimer() {
  if (hideTimer === null) return;
  clearTimeout(hideTimer);
  hideTimer = null;
}

function shouldKeepFocusChromeVisible() {
  if (typeof document === "undefined") return false;

  const activeElement = document.activeElement;
  if (
    activeElement instanceof Element &&
    activeElement.closest(
      `${FOCUS_CHROME_REGION_SELECTOR}, ${FOCUS_CHROME_FLOATING_SELECTOR}`,
    )
  ) {
    return true;
  }

  return document.querySelector(FOCUS_CHROME_FLOATING_SELECTOR) !== null;
}

function queueHide(set: (state: Partial<FocusChromeAutoHideState>) => void) {
  clearHideTimer();
  hideTimer = setTimeout(() => {
    hideTimer = null;
    if (shouldKeepFocusChromeVisible()) {
      queueHide(set);
      return;
    }
    set({ revealed: false });
  }, FOCUS_CHROME_HIDE_DELAY_MS);
}

const useFocusChromeAutoHideStore = create<FocusChromeAutoHideState>((set) => ({
  revealed: false,
  reveal: () => {
    clearHideTimer();
    set({ revealed: true });
  },
  scheduleHide: () => {
    queueHide(set);
  },
  reset: () => {
    clearHideTimer();
    set({ revealed: false });
  },
}));

function useFocusChromeAutoHide() {
  const focusActive = usePresentationStore(
    (s) => s.presentation?.variant === "focus",
  );
  const revealed = useFocusChromeAutoHideStore((s) => s.revealed);
  const reveal = useFocusChromeAutoHideStore((s) => s.reveal);
  const scheduleHide = useFocusChromeAutoHideStore((s) => s.scheduleHide);
  const reset = useFocusChromeAutoHideStore((s) => s.reset);

  useEffect(() => {
    if (!focusActive) reset();
  }, [focusActive, reset]);

  const focusChromeRegionProps = useMemo(
    () => ({
      "data-focus-chrome-region": "true",
      onBlurCapture: ((event) => {
        if (!event.relatedTarget) return;
        if (focusActive) scheduleHide();
      }) satisfies FocusEventHandler,
      onFocusCapture: (() => {
        if (focusActive) reveal();
      }) satisfies FocusEventHandler,
      onPointerEnter: (() => {
        if (focusActive) reveal();
      }) satisfies PointerEventHandler,
      onPointerLeave: ((event) => {
        if (event.pointerType === "touch") return;
        if (focusActive) scheduleHide();
      }) satisfies PointerEventHandler,
    }),
    [focusActive, reveal, scheduleHide],
  );

  return {
    focusActive,
    focusChromeHidden: focusActive && !revealed,
    focusChromeRegionProps,
    revealFocusChrome: reveal,
  };
}

export {
  FOCUS_CHROME_HIDE_DELAY_MS,
  useFocusChromeAutoHide,
  useFocusChromeAutoHideStore,
};
